import { Button } from '@lightning/ui/button';
import { HowieActionButton } from '@/components/HowieActionButton';
import type { DojoImperativeProgress, DojoJudgeSummary } from '@/types/dojo';

interface RoundCompleteProps {
  imperativeLabel: string;
  imperativeId: string;
  imperativeProgress: DojoImperativeProgress | null;
  judgeSummary: DojoJudgeSummary | null;
  savedReflection?: string;
  onNextUnit?: () => void;
  onBackToPractice: () => void;
  onViewYourHow?: () => void;
  disabled?: boolean;
}

/**
 * A quiet close with something to carry forward. Returning to the Dojo leads;
 * another practice remains optional, with no score or mastery claim.
 */
export function RoundComplete({
  imperativeLabel,
  savedReflection,
  onNextUnit,
  onBackToPractice,
  onViewYourHow,
  disabled = false,
}: RoundCompleteProps) {

  return (
    <div className="mx-auto max-w-2xl rounded-2xl border border-how-space-deep/[0.05] bg-how-paper/55 p-6">
      <span className="text-xs font-normal text-how-text-secondary">
        {imperativeLabel}
      </span>

      <h2 className="mt-3 font-body text-[24px] font-medium leading-[30px] text-how-space-deep">
        {savedReflection?.trim() ? 'Reflection saved.' : 'Practice complete.'}
      </h2>
      {savedReflection?.trim() && (
        <blockquote className="mt-4 whitespace-pre-wrap break-words font-body text-[16px] leading-6 text-how-space-deep">
          {savedReflection}
        </blockquote>
      )}

      {/* Judge fields are internal coaching directives, not learner-facing feedback. */}

      <div className="mt-6 flex flex-wrap gap-2">
        <HowieActionButton onClick={onBackToPractice} disabled={disabled}>
          Back to Dojo
        </HowieActionButton>
        {onNextUnit && (
          <Button
            variant="ghost"
            className="h-9 rounded-full px-4 font-body text-[13px] font-normal text-how-space-deep"
            type="button"
            onClick={onNextUnit}
            disabled={disabled}
          >
            Next practice
          </Button>
        )}
      </div>

      {onViewYourHow && (
        <button
          type="button"
          onClick={onViewYourHow}
          className="mt-4 text-[13px] text-how-text-secondary/60 underline decoration-how-text-secondary/20 underline-offset-4 transition-colors hover:text-how-space-deep hover:decoration-how-space-deep/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-accent-warm/40"
        >
          See what this added to Your HOW
        </button>
      )}
    </div>
  );
}

export default RoundComplete;
